import { getFeatureFlags } from 'core/flow-control';
import { BackendAttributeFilter, FilterOperator, VisitDurationType } from '@placer-ui/types';
import { LENGTH_OF_STAY_ATTRIBUTE, MORE_THEN_FILTER_OPERATOR, LESS_THEN_FILTER_OPERATOR, ANY_FILTER_OPERATOR } from 'shared/core/constants/filter-attributes';

export const BOTTOM_VALUE_10_MIN_LONGER_PROFILE = 10;
export const MIN_10_MIN_LONGER_PROFILE_VISIT_DURATION_VALUE_GREATER_THAN_DEFAULT = 5;
export const MIN_10_MIN_LONGER_PROFILE_VISIT_DURATION_VALUE_LESS_THAN_DEFAULT = 10;

/**
 * Feature flag check for daily duration filter
 */
export const isDailyDurationFilterEnabled = (isPosie: boolean): boolean => {
    const { enable_daily_duration_filter_posie_property_ff } = getFeatureFlags();
    return !!enable_daily_duration_filter_posie_property_ff && isPosie;
};

/**
 * Creates visit duration filter attribute with configurable operator
 */
export const createVisitDurationFilter = (
    value: number,
    operator: FilterOperator = MORE_THEN_FILTER_OPERATOR,
): BackendAttributeFilter => {
    return [operator, LENGTH_OF_STAY_ATTRIBUTE, value];
};

/**
 * Check if any entity meets the criteria for disabling visit duration options
 */
export const shouldDisableVisitDurationOption = (
    optionValue: VisitDurationType | string,
    entities: Array<{ originalEntity: { isPosie?: boolean; default_profile_name?: VisitDurationType | string } }>,
): boolean => {
    // Check if any entity is posie with default profile of 10_min_or_longer
    const hasRestrictedEntity = entities.some(
        ({ originalEntity }) => {
            const isPosie = !!originalEntity?.isPosie;
            const defaultProfileName = originalEntity?.default_profile_name;
            return isDailyDurationFilterEnabled(isPosie) &&
                (
                    defaultProfileName === '10_min_or_longer' ||
                    defaultProfileName === 'default' ||
                    defaultProfileName === 'market_visitors'
                );
        },
    );

    if (!hasRestrictedEntity) return false;

    return optionValue === 'under_10_min' || optionValue === 'all_visits';
};

export const shouldDisableVisitDurationNumericOption = (
    value: number,
    operator: FilterOperator,
    isPosie: boolean,
    minVisitDuration: number,
): boolean => {
    if (!isDailyDurationFilterEnabled(isPosie) || minVisitDuration !== 10) return false;

    if (operator === MORE_THEN_FILTER_OPERATOR && value <= MIN_10_MIN_LONGER_PROFILE_VISIT_DURATION_VALUE_GREATER_THAN_DEFAULT) {
        return true;
    } else if (operator === LESS_THEN_FILTER_OPERATOR && value <= MIN_10_MIN_LONGER_PROFILE_VISIT_DURATION_VALUE_LESS_THAN_DEFAULT) {
        return true;
    }

    return false;
};

export const shouldDisableVisitDurationOperatorOption = (
    operator: FilterOperator,
    isPosie: boolean,
    minVisitDuration: number,
): boolean => {
    if (!isDailyDurationFilterEnabled(isPosie)) return false;
    if (operator === ANY_FILTER_OPERATOR && minVisitDuration === 10) {
        return true;
    }

    return false;
};

/**
 * Gets tooltip text for disabled options
 */
export const getDisabledOptionTooltip = (entityCount: number = 1): string => {
    if (entityCount > 1) {
        return 'This duration is not supported for this comparison';
    }
    return 'This duration is not supported for this property';
};

